import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Copy, Check, Shield, Sparkles, Globe, Mail, Building2, FileText, Eye, Users, Inbox, RefreshCw, Flame, Snowflake, Sun, Tag } from "lucide-react";
import CinematicNav from "@/components/CinematicNav";
import Footer from "@/components/Footer";
import ModuleAccent from "@/components/ModuleAccent";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export default function AriaWidget() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        site_url: "",
        site_name: "",
        vertical: "ristorante",
        privacy_email: "",
        accept_dpa: false,
        accept_privacy: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [snippet, setSnippet] = useState(null);
    const [copied, setCopied] = useState(false);
    const [myWidgets, setMyWidgets] = useState([]);
    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [reenriching, setReenriching] = useState(null);

    useEffect(() => {
        document.title = "Aria — Il tuo Assistente · TotalKlick";
    }, []);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const res = await api.get("/widget/me");
                setMyWidgets(res.data || []);
            } catch (e) { /* silent */ }
        })();
    }, [user]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const res = await api.get("/widget/leads?limit=20");
                setLeads(res.data || []);
            } catch (e) { /* silent */ }
        })();
    }, [user]);

    const loadLeads = async () => {
        setLeadsLoading(true);
        try {
            const res = await api.get("/widget/leads?limit=20");
            setLeads(res.data || []);
        } catch (e) { /* silent */ }
        finally { setLeadsLoading(false); }
    };

    const reenrichLead = async (leadId) => {
        setReenriching(leadId);
        try {
            await api.post(`/widget/leads/${leadId}/reenrich`);
            const res = await api.get("/widget/leads?limit=20");
            setLeads(res.data || []);
        } catch (e) { /* silent */ }
        finally { setReenriching(null); }
    };

    if (!user) {
        return (
            <ModuleAccent module="aria" as="div" className="min-h-screen" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-widget-page">
                <CinematicNav />
                <div className="max-w-md mx-auto pt-32 px-5 text-center">
                    <Shield size={32} className="mx-auto mb-5 text-[#4F8BFF]" />
                    <h1 className="text-white text-3xl mb-3" style={{ fontWeight: 200, letterSpacing: "-0.03em" }}>Login richiesto</h1>
                    <p className="text-white/55 mb-6">Per creare il tuo assistente Aria devi essere autenticato. È gratuito.</p>
                    <Link to="/login" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", boxShadow: "0 18px 50px -10px rgba(79,139,255,0.6)" }}>
                        Accedi <ArrowRight size={15} />
                    </Link>
                </div>
            </ModuleAccent>
        );
    }

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.accept_dpa) return setError("Devi accettare il DPA per generare l'assistente.");
        if (!form.accept_privacy) return setError("Devi confermare di mostrare l'informativa privacy sul tuo sito.");
        setLoading(true);
        try {
            const res = await api.post("/widget/register", form);
            setSnippet(res.data);
            setMyWidgets((prev) => [res.data, ...prev]);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const copy = () => {
        if (!snippet?.snippet) return;
        navigator.clipboard.writeText(snippet.snippet);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <ModuleAccent module="aria" as="div" className="min-h-screen w-full overflow-x-hidden" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-widget-page">
            <CinematicNav />

            {user && (
                <div className="px-5 md:px-12 pt-24 pb-0 max-w-7xl mx-auto">
                    <Link to="/app/aria" className="inline-flex items-center gap-1.5 text-[12px] text-[#9EC3FF] hover:text-white transition" data-testid="goto-dashboard">
                        <ArrowRight size={11} /> Vai alla Dashboard Aria
                    </Link>
                </div>
            )}

            <section className="relative px-5 md:px-12 pt-8 pb-12 md:pt-12">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                        style={{ background: "var(--mod-accent-soft)", border: "1px solid var(--mod-accent)" }}>
                        <Sparkles size={11} className="text-[#4F8BFF]" />
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#4F8BFF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            il tuo assistente · gratuito · gdpr ready
                        </span>
                    </div>
                    <h1 className="text-white tracking-tight" style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2.2rem, 5vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.035em" }}>
                        Aria sul tuo sito.<br />
                        <span style={{ fontWeight: 600 }}>Lead qualificati. Database tuo.</span>
                    </h1>
                    <p className="mt-5 text-white/65 max-w-2xl mx-auto" style={{ fontFamily: "Outfit, sans-serif", fontSize: "1.05rem", lineHeight: 1.55 }}>
                        Installa Aria gratis sul tuo sito web in 2 minuti. Qualifica i visitatori, raccoglie email,
                        capisce il loro problema. Tu vedi un database pulito, filtrato, GDPR-compliant.
                    </p>
                </div>
            </section>

            <section className="px-5 md:px-12 pb-24">
                <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-6 md:gap-8">
                    {/* FORM */}
                    <div className="lg:col-span-3 rounded-3xl p-7 md:p-9"
                        style={{ background: "rgba(15,18,36,0.65)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(20px) saturate(140%)", WebkitBackdropFilter: "blur(20px) saturate(140%)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5)" }}>
                        {!snippet ? (
                            <form onSubmit={submit} className="space-y-5" data-testid="aria-widget-form">
                                <SectionTitle n="01" title="Il tuo sito" />
                                <Field icon={Globe} label="URL del tuo sito *">
                                    <input data-testid="ariaw-url" required value={form.site_url} onChange={(e) => set("site_url", e.target.value)} placeholder="https://tuosito.it" className="cine-input pl-11" />
                                </Field>
                                <Field icon={Building2} label="Nome attività *">
                                    <input data-testid="ariaw-name" required value={form.site_name} onChange={(e) => set("site_name", e.target.value)} placeholder="es. Pizzeria Da Mario" className="cine-input pl-11" />
                                </Field>
                                <div>
                                    <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                        Settore *
                                    </label>
                                    <select data-testid="ariaw-vertical" value={form.vertical} onChange={(e) => set("vertical", e.target.value)} className="cine-input">
                                        <option value="ristorante">Ristorante / Bar</option>
                                        <option value="tipografia">Tipografia / Stampa</option>
                                        <option value="agenzia">Agenzia / SMM</option>
                                        <option value="ecommerce">E-commerce</option>
                                        <option value="immobiliare">Immobiliare</option>
                                        <option value="servizi">Servizi professionali</option>
                                        <option value="negozio">Negozio fisico</option>
                                        <option value="altro">Altro</option>
                                    </select>
                                </div>

                                <SectionTitle n="02" title="Privacy & DPO" />
                                <Field icon={Mail} label="Email del referente privacy del sito *">
                                    <input data-testid="ariaw-privacy-email" type="email" required value={form.privacy_email} onChange={(e) => set("privacy_email", e.target.value)} placeholder="privacy@tuosito.it" className="cine-input pl-11" />
                                </Field>
                                <p className="text-[11.5px] text-white/45 -mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    Necessaria per gli adempimenti GDPR. Sarà inserita nell&apos;informativa template che ti forniamo.
                                </p>

                                <SectionTitle n="03" title="Consensi obbligatori" />
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <input data-testid="ariaw-dpa" type="checkbox" checked={form.accept_dpa} onChange={(e) => set("accept_dpa", e.target.checked)} className="mt-1 w-4 h-4 cursor-pointer" style={{ accentColor: "#4F8BFF" }} />
                                    <span className="text-[12.5px] leading-[1.55] text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        Accetto il <a href="/legal/dpa-aria-widget" target="_blank" rel="noreferrer" className="text-[#4F8BFF] hover:text-white font-medium">DPA — Data Processing Agreement</a>: TotalKlick srls agisce come <b className="text-white">responsabile del trattamento</b> per conto del mio sito (titolare).
                                    </span>
                                </label>
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <input data-testid="ariaw-privacy" type="checkbox" checked={form.accept_privacy} onChange={(e) => set("accept_privacy", e.target.checked)} className="mt-1 w-4 h-4 cursor-pointer" style={{ accentColor: "#4F8BFF" }} />
                                    <span className="text-[12.5px] leading-[1.55] text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        Mi impegno a inserire sul mio sito l&apos;<a href="/legal/privacy-template-widget" target="_blank" rel="noreferrer" className="text-[#4F8BFF] hover:text-white font-medium">informativa privacy template</a> fornita da TotalKlick, adattandola alla mia attività.
                                    </span>
                                </label>

                                {error && (
                                    <div className="px-3 py-2.5 rounded-lg text-[13px]" style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.3)", color: "#FCA5A5" }} data-testid="ariaw-error">
                                        {error}
                                    </div>
                                )}

                                <button data-testid="ariaw-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.02] disabled:opacity-50"
                                    style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", fontFamily: "Geist, sans-serif", letterSpacing: "0.04em", boxShadow: "0 18px 50px -10px rgba(79,139,255,0.6)" }}>
                                    {loading ? "Generazione…" : <>Genera il mio assistente gratis <ArrowRight size={14} /></>}
                                </button>
                            </form>
                        ) : (
                            <SnippetResult snippet={snippet} copied={copied} onCopy={copy} onReset={() => setSnippet(null)} />
                        )}
                    </div>

                    {/* SIDE INFO */}
                    <div className="lg:col-span-2 space-y-3">
                        <InfoCard icon={<Eye size={14} className="text-[#4F8BFF]" />} title="Cosa fa l'assistente">
                            Mostra un&apos;icona fluttuante in basso a destra del tuo sito. Quando un visitatore clicca, Aria si apre e conversa: capisce settore, intento, urgenza. Cattura email solo dopo aver fornito valore.
                        </InfoCard>
                        <InfoCard icon={<Users size={14} className="text-[#FBBF24]" />} title="Database qualificato">
                            Ogni lead arriva già <b className="text-white">profilato</b>: settore, intento, urgenza, conversazione intera. Filtrabili dal tuo Workspace. Esportabili CSV.
                        </InfoCard>
                        <InfoCard icon={<Shield size={14} className="text-[#10B981]" />} title="GDPR-ready">
                            Banner consenso integrato, IP hashed, periodo conservazione 24 mesi, diritti dell&apos;interessato gestiti. Informativa template italiana pronta + DPA firmabile.
                        </InfoCard>
                        <InfoCard icon={<Sparkles size={14} className="text-[#A855F7]" />} title="100% gratuito">
                            Per sempre. La nostra retribuzione? Quando un utente del tuo assistente diventa cliente TotalKlick (es. attiva TotalPL), riceve uno sconto e tu — se vuoi — un&apos;affiliate fee opzionale.
                        </InfoCard>

                        {myWidgets.length > 0 && (
                            <div className="rounded-2xl p-4 mt-4" style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.07)" }}>
                                <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    I tuoi assistenti ({myWidgets.length})
                                </div>
                                <div className="space-y-2">
                                    {myWidgets.slice(0, 3).map((w) => (
                                        <div key={w.widget_token} className="px-3 py-2 rounded-lg text-[12px]" style={{ background: "rgba(255,255,255,0.03)" }}>
                                            <div className="text-white truncate" style={{ fontWeight: 500 }}>{w.site_name}</div>
                                            <div className="text-white/45 text-[10.5px] truncate font-mono">{w.site_url}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* === FASE 3 — Lead Inbox con CRM auto-tagging === */}
            <section className="px-5 md:px-12 pb-24" data-testid="aria-leads-section">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
                        <div>
                            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 mb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>lead inbox · ai-tagged</div>
                            <h2 className="text-white tracking-tight" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.5rem,2.6vw,2.2rem)", letterSpacing: "-0.025em" }}>
                                I lead <span style={{ fontWeight: 600 }}>arricchiti</span> dall&apos;AI
                            </h2>
                            <p className="text-white/50 mt-1 text-[13px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Aria analizza ogni conversazione e classifica i contatti: intent (cold/warm/hot), tag, categoria, bisogni futuri.
                            </p>
                        </div>
                        <button type="button" onClick={loadLeads} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] text-white/85 hover:text-white transition" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="leads-refresh">
                            <RefreshCw size={12} className={leadsLoading ? "animate-spin" : ""} /> Aggiorna
                        </button>
                    </div>

                    {leads.length === 0 ? (
                        <div className="rounded-2xl p-10 text-center" style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="leads-empty">
                            <Inbox size={28} className="mx-auto mb-3 text-white/30" />
                            <div className="text-white/70 text-[14px]" style={{ fontFamily: "Geist, sans-serif" }}>Ancora nessun lead</div>
                            <div className="text-white/40 text-[12px] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>I lead catturati dalle tue istanze Aria appariranno qui con tag automatici.</div>
                        </div>
                    ) : (
                        <div className="space-y-2.5" data-testid="leads-list">
                            {leads.map((lead) => (
                                <LeadRow key={lead.lead_id} lead={lead} onReenrich={() => reenrichLead(lead.lead_id)} busy={reenriching === lead.lead_id} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer variant="hub" />

            <style>{`
                .cine-input { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.03); color: #fff; font-family: 'Geist','Outfit',sans-serif; font-size: 14px; transition: all 0.2s; }
                .cine-input.cine-input { padding-left: 14px; }
                input.cine-input.pl-11, textarea.cine-input.pl-11 { padding-left: 44px; }
                .cine-input:focus { outline: none; border-color: rgba(79,139,255,0.55); background: rgba(255,255,255,0.05); box-shadow: 0 0 0 3px rgba(79,139,255,0.12); }
                .cine-input::placeholder { color: rgba(255,255,255,0.3); }
                .cine-input option { background: #0F1224; color: #fff; }
            `}</style>
        </ModuleAccent>
    );
}

function SnippetResult({ snippet, copied, onCopy, onReset }) {
    return (
        <div className="space-y-5" data-testid="aria-widget-result">
            <div className="text-center">
                <div className="inline-grid place-items-center w-14 h-14 rounded-2xl mb-4" style={{ background: "linear-gradient(135deg, #10B981, #047857)", boxShadow: "0 12px 40px -10px rgba(16,185,129,0.5)" }}>
                    <Check size={26} className="text-white" />
                </div>
                <div className="text-white text-[22px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500, letterSpacing: "-0.02em" }}>
                    Assistente pronto!
                </div>
                <p className="text-white/60 text-[13.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Copia il codice qui sotto e incollalo prima del <code className="text-[#4F8BFF]">&lt;/body&gt;</code> del tuo sito.
                </p>
            </div>

            <div className="relative rounded-xl overflow-hidden" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>html · paste before &lt;/body&gt;</span>
                    <button onClick={onCopy} type="button" className="inline-flex items-center gap-1.5 text-[11px] text-[#4F8BFF] hover:text-white transition" data-testid="ariaw-copy">
                        {copied ? <><Check size={11} /> Copiato!</> : <><Copy size={11} /> Copia</>}
                    </button>
                </div>
                <pre className="px-4 py-3 text-[12px] text-white/85 overflow-x-auto" style={{ fontFamily: "JetBrains Mono, monospace", lineHeight: 1.6 }}>
{snippet.snippet}
                </pre>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <a href={snippet.privacy_template_url} target="_blank" rel="noreferrer" className="px-4 py-3 rounded-xl border border-white/12 text-white/85 hover:text-white hover:bg-white/5 transition text-[12.5px] flex items-start gap-2" data-testid="ariaw-privacy-template">
                    <FileText size={14} className="mt-0.5 text-[#FBBF24] flex-none" />
                    <div>
                        <div className="font-medium" style={{ fontFamily: "Geist, sans-serif" }}>Informativa privacy</div>
                        <div className="text-white/45 text-[11px]">Template italiano da incollare</div>
                    </div>
                </a>
                <a href={snippet.dpa_url} target="_blank" rel="noreferrer" className="px-4 py-3 rounded-xl border border-white/12 text-white/85 hover:text-white hover:bg-white/5 transition text-[12.5px] flex items-start gap-2" data-testid="ariaw-dpa-link">
                    <Shield size={14} className="mt-0.5 text-[#10B981] flex-none" />
                    <div>
                        <div className="font-medium" style={{ fontFamily: "Geist, sans-serif" }}>DPA — Accordo accettato</div>
                        <div className="text-white/45 text-[11px]">Scarica una copia per i tuoi archivi</div>
                    </div>
                </a>
            </div>

            <button onClick={onReset} type="button" className="text-[12.5px] text-white/55 hover:text-white transition" data-testid="ariaw-create-another">
                ← Crea un altro assistente
            </button>
        </div>
    );
}

function SectionTitle({ n, title }) {
    return (
        <div className="flex items-center gap-3 pt-1">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>{n}</span>
            <span className="text-white text-[14.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{title}</span>
        </div>
    );
}

function Field({ icon: Icon, label, children }) {
    return (
        <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {label}
            </label>
            <div className="relative">
                {Icon && <Icon size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />}
                {children}
            </div>
        </div>
    );
}

function InfoCard({ icon, title, children }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: EASE }}
            className="rounded-xl p-4"
            style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.07)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
        >
            <div className="flex items-center gap-2 mb-2 text-white text-[13px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                {icon} {title}
            </div>
            <p className="text-white/55 text-[12.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                {children}
            </p>
        </motion.div>
    );
}

// ============== FASE 3 — Lead Inbox UI ==============

function LeadRow({ lead, onReenrich, busy }) {
    const created = lead.created_at ? new Date(lead.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
    const enriched = !!lead.intent_level;
    const intent = (lead.intent_level || "").toLowerCase();
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl p-4 md:p-5 transition hover:bg-white/[0.025]"
            style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}
            data-testid={`lead-row-${lead.lead_id}`}
        >
            <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
                {/* Intent badge */}
                <IntentBadge intent={intent} />

                {/* Identità + contatti */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-[14px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                            {lead.name || "(senza nome)"}
                        </span>
                        {lead.goal_achieved && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded text-emerald-400" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", fontFamily: "JetBrains Mono, monospace" }}>
                                <Check size={9} /> goal
                            </span>
                        )}
                    </div>
                    <div className="text-white/55 text-[12px] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {[lead.email, lead.phone].filter(Boolean).join(" · ") || "(no contatti)"}
                    </div>
                    <div className="text-white/35 text-[10.5px] mt-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {lead.site_name} · {created}
                    </div>

                    {/* Summary AI */}
                    {lead.summary && (
                        <p className="text-white/70 text-[12.5px] mt-2 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
                            “{lead.summary}”
                        </p>
                    )}

                    {/* Tags + category + future_needs */}
                    {enriched && (
                        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`lead-tags-${lead.lead_id}`}>
                            {lead.category && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] text-[#9EC3FF]" style={{ background: "rgba(79,139,255,0.1)", border: "1px solid rgba(79,139,255,0.3)" }}>
                                    <Tag size={9} /> {lead.category}
                                </span>
                            )}
                            {(lead.tags || []).map((t, i) => (
                                <span key={`t${i}`} className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] text-white/80" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>#{t}</span>
                            ))}
                            {(lead.future_needs || []).slice(0, 3).map((f, i) => (
                                <span key={`f${i}`} className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] text-[#FBBF24]" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)" }}>→ {f}</span>
                            ))}
                        </div>
                    )}

                    {!enriched && !lead.enrichment_error && (
                        <div className="text-white/35 text-[11px] mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>· arricchimento in corso…</div>
                    )}
                    {lead.enrichment_error && (
                        <div className="text-rose-300/70 text-[11px] mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>arricchimento fallito · clicca per riprovare</div>
                    )}
                </div>

                {/* Re-enrich */}
                <button type="button" onClick={onReenrich} disabled={busy} className="flex-none text-white/45 hover:text-white text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition disabled:opacity-40" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`lead-reenrich-${lead.lead_id}`}>
                    <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> {busy ? "Analisi…" : "Re-analizza"}
                </button>
            </div>
        </motion.div>
    );
}

function IntentBadge({ intent }) {
    const map = {
        hot: { Icon: Flame, color: "#F97316", bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.4)", label: "HOT" },
        warm: { Icon: Sun, color: "#FBBF24", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.4)", label: "WARM" },
        cold: { Icon: Snowflake, color: "#7DD3FC", bg: "rgba(125,211,252,0.12)", border: "rgba(125,211,252,0.4)", label: "COLD" },
    };
    const m = map[intent] || { Icon: Snowflake, color: "rgba(255,255,255,0.5)", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)", label: "—" };
    const I = m.Icon;
    return (
        <div className="flex-none w-16 grid place-items-center rounded-xl py-2.5" style={{ background: m.bg, border: `1px solid ${m.border}` }} title={`Intent: ${m.label}`}>
            <I size={16} color={m.color} />
            <span className="text-[9.5px] mt-1 tracking-[0.2em]" style={{ fontFamily: "JetBrains Mono, monospace", color: m.color, fontWeight: 600 }}>{m.label}</span>
        </div>
    );
}
